import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const LenderPerformance = () => {
  const { toast } = useToast();
  const { data: lenders, isLoading, error } = useQuery({
    queryKey: ["lenders-performance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lenders")
        .select("*")
        .order("total_loans_funded", { ascending: false });

      if (error) {
        console.error("Error fetching lenders:", error);
        toast({
          title: "Error",
          description: "Failed to load lender performance data",
          variant: "destructive",
        });
        throw error;
      }
      return data;
    },
  });

  const performanceData = lenders?.map((lender) => ({
    name: lender.institution_name,
    loans: lender.total_loans_funded || 0,
  }));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-center justify-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
            <p className="text-red-500">Failed to load lender performance data</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!lenders?.length) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
            <p className="text-muted-foreground">No lender data available</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">Loan Performance</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="loans" fill="#8884d8" name="Total Loans Funded" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {lenders?.map((lender) => (
        <Card key={lender.id} className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{lender.institution_name}</h3>
              <span className="text-sm text-muted-foreground">
                {lender.risk_appetite || "Risk appetite not set"}
              </span>
            </div>
            
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Loans Funded</p>
                <p className="text-2xl font-bold">{lender.total_loans_funded || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">License Status</p>
                <p className="text-2xl font-bold">{lender.license_number ? "Licensed" : "Unlicensed"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Risk Level</p>
                <p className="text-2xl font-bold capitalize">{lender.risk_appetite || "N/A"}</p>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default LenderPerformance;